import { useState } from 'react';

import Modal from './ui/Modal';
import Alert from './ui/Alert';
import ManagementAuth from '../services/ManagementAuth';

interface ManagementPinModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const ManagementPinModal = ({ open, onClose, onSuccess }: ManagementPinModalProps) => {
  const [pin, setPin] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setPin('');
    setError(null);
    onClose();
  };

  const handleSubmit = async () => {
    if (!pin || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await ManagementAuth.login(pin);
      setPin('');
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to sign in.");
      setPin('');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Management Login"
      actions={
        <>
          <button
            type="button"
            onClick={handleClose}
            className="rounded px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || !pin}
            className="rounded bg-themedaccent px-3 py-1.5 text-sm font-medium text-white hover:bg-themedaccent/80 disabled:opacity-50"
          >
            {submitting ? "Signing in..." : "Sign in"}
          </button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <label className="text-sm text-gray-700" htmlFor="management-pin">
          Enter the management PIN to continue.
        </label>
        <input
          id="management-pin"
          type="password"
          inputMode="numeric"
          autoFocus
          value={pin}
          onChange={(e) => setPin(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
          className="rounded border border-gray-300 px-3 py-2 text-sm"
        />
        {error && <Alert severity="error">{error}</Alert>}
      </div>
    </Modal>
  );
};

export default ManagementPinModal;
